import { useEffect, useRef, useState } from 'react';
import { AccountMenu } from './components/AccountMenu';
import { BrandSplash } from './components/BrandSplash';
import { LanguageSelector, useI18n } from './i18n';
import { CurrentUser, api } from './lib/api';
import { DashboardPage } from './pages/DashboardPage';
import { LivePage } from './pages/LivePage';
import { LoginPage } from './pages/LoginPage';
import { PlaybackPage } from './pages/PlaybackPage';
import { SystemStatusPage } from './pages/SystemStatusPage';
import { UsersPage } from './pages/UsersPage';
import { VideoStreamsPage } from './pages/VideoStreamsPage';

type Page = 'dashboard' | 'live' | 'playback' | 'streams' | 'system';
type LoginPhase = 'idle' | 'fade-out' | 'splash';

type NavigationItem = {
  page: Page;
  labelKey: string;
  adminOnly?: boolean;
};

const navigationItems: NavigationItem[] = [
  { page: 'dashboard', labelKey: 'nav.overview' },
  { page: 'live', labelKey: 'nav.live' },
  { page: 'playback', labelKey: 'nav.playback' },
  { page: 'streams', labelKey: 'nav.streams', adminOnly: true },
  { page: 'system', labelKey: 'nav.system', adminOnly: true },
];

export default function App() {
  const { t } = useI18n();
  const [user, setUser] = useState<CurrentUser>();
  const [authResolved, setAuthResolved] = useState(false);
  const [bootCompleted, setBootCompleted] = useState(false);
  const [pendingUser, setPendingUser] = useState<CurrentUser>();
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('idle');
  const [page, setPage] = useState<Page>('dashboard');
  const [usersOpen, setUsersOpen] = useState(false);
  const [liveMounted, setLiveMounted] = useState(false);
  const liveLayerRef = useRef<HTMLDivElement | null>(null);
  const loginTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    void api<CurrentUser>('/auth/me')
      .then(setUser)
      .catch(() => setUser(undefined))
      .finally(() => setAuthResolved(true));
  }, []);

  useEffect(() => () => {
    if (loginTimerRef.current !== undefined) {
      window.clearTimeout(loginTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const liveLayer = liveLayerRef.current;
    if (!liveMounted || !liveLayer) return;

    if (page === 'live') {
      liveLayer.removeAttribute('inert');
      return;
    }

    liveLayer.setAttribute('inert', '');
  }, [liveMounted, page]);

  function beginLoginTransition(nextUser: CurrentUser) {
    setPendingUser(nextUser);
    setLoginPhase('fade-out');
    if (loginTimerRef.current !== undefined) window.clearTimeout(loginTimerRef.current);
    loginTimerRef.current = window.setTimeout(() => {
      loginTimerRef.current = undefined;
      setLoginPhase('splash');
    }, 240);
  }

  if (!bootCompleted) {
    return (
      <BrandSplash
        ready={authResolved}
        onComplete={() => setBootCompleted(true)}
      />
    );
  }

  if (loginPhase === 'splash' && pendingUser) {
    return (
      <BrandSplash
        mode="login"
        ready
        onComplete={() => {
          setUser(pendingUser);
          setPendingUser(undefined);
          setLoginPhase('idle');
          setPage('dashboard');
        }}
      />
    );
  }

  if (!user) {
    return (
      <div className={loginPhase === 'fade-out' ? 'screen-fade-out' : 'screen-fade-in'}>
        <LoginPage onLogin={beginLoginTransition} />
      </div>
    );
  }

  async function logout() {
    await api<void>('/auth/logout', { method: 'POST' });
    setUsersOpen(false);
    setLiveMounted(false);
    setPage('dashboard');
    setPendingUser(undefined);
    setLoginPhase('idle');
    setUser(undefined);
  }

  function navigate(next: Page) {
    if ((next === 'streams' || next === 'system') && user?.role !== 'ADMIN') {
      setPage('dashboard');
      return;
    }
    if (next === 'live') {
      setLiveMounted(true);
    }
    setPage(next);
  }

  const availableNavigation = navigationItems.filter((item) => !item.adminOnly || user.role === 'ADMIN');

  return (
    <div className="app-shell top-navigation-shell page-fade-in">
      <header className="global-topbar">
        <button className="topbar-brand" onClick={() => navigate('dashboard')} aria-label={t('app.returnOverview')}>
          <span className="brand-mark compact">OAPM</span>
          <strong>{t('app.name')}</strong>
        </button>

        <nav className="topbar-navigation" aria-label={t('nav.main')}>
          {availableNavigation.map((item) => (
            <button
              key={item.page}
              className={page === item.page ? 'active' : ''}
              onClick={() => navigate(item.page)}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </nav>

        <div
          id="live-notice-slot"
          className={`topbar-live-notice-slot ${page === 'live' ? 'is-active' : 'is-inactive'}`}
          aria-live="polite"
          aria-hidden={page !== 'live'}
        />
        <div
          id="live-layout-controls"
          className={`topbar-live-layout-slot ${page === 'live' ? 'is-active' : 'is-inactive'}`}
          aria-label={t('nav.live')}
          aria-hidden={page !== 'live'}
        />

        <LanguageSelector className="topbar-language-selector" />

        <AccountMenu
          user={user}
          onUserChanged={setUser}
          onOpenUsers={() => setUsersOpen(true)}
          onLogout={logout}
        />
      </header>

      <main className="content-shell persistent-page-host">
        {liveMounted && (
          <div
            ref={liveLayerRef}
            className={`persistent-live-layer ${page === 'live' ? 'is-active' : 'is-standby'}`}
            aria-hidden={page !== 'live'}
            data-live-workspace-state={page === 'live' ? 'foreground' : 'background'}
          >
            <LivePage />
          </div>
        )}

        {page !== 'live' && (
          <div className="route-page-layer page-fade-in">
            {page === 'dashboard' && <DashboardPage onNavigate={(target) => navigate(target)} canManageStreams={user.role === 'ADMIN'} />}
            {page === 'playback' && <PlaybackPage />}
            {page === 'streams' && user.role === 'ADMIN' && <VideoStreamsPage />}
            {page === 'system' && user.role === 'ADMIN' && <SystemStatusPage />}
          </div>
        )}
      </main>

      {usersOpen && user.role === 'ADMIN' && <UsersPage currentUser={user} onClose={() => setUsersOpen(false)} />}
    </div>
  );
}
